import { BuildingIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { Input } from '@ems/ui/components/input'
import { Combobox, type ComboboxOption } from '@ems/ui/patterns/combobox'
import { Field } from '@ems/ui/patterns/field'

import type { Company } from '#admin/features/exhibitor/companies-api.ts'
import { DeskSection } from '#admin/features/exhibitor/onboarding/desk-section.tsx'
import type { DeskDraft } from '#admin/features/exhibitor/onboarding/draft.ts'

/**
 * Who books the stand: a company the edition knows, or a new one, and the person to contact.
 *
 * A company that has an active application is not in the list. The server allows one active
 * application for each company, and refuses a second one.
 */
export function CompanySection({
  draft,
  onChange,
  issues,
  companies,
  booked,
  locked,
  variant,
}: {
  draft: DeskDraft
  onChange: (changes: Partial<DeskDraft>) => void
  issues: Readonly<Record<string, string>>
  companies: readonly Company[]
  /** The companies that have an active application. */
  booked: ReadonlySet<string>
  locked: boolean
  variant: 'exhibition' | 'sponsor'
}) {
  const sponsor = variant === 'sponsor'
  // The sponsor desk takes a sponsor company too. The run records it as an exhibitor as well.
  const listed = (company: Company) =>
    company.kinds.includes('exhibitor') || (sponsor && company.kinds.includes('sponsor'))
  // The chosen company stays in the list: after a failed run, its new application books it.
  const free = companies.filter(
    (company) => listed(company) && (!booked.has(company.id) || company.id === draft.companyId),
  )
  const hidden = companies.filter(
    (company) => booked.has(company.id) && company.id !== draft.companyId,
  ).length
  const mode = (companyMode: DeskDraft['companyMode']) => (
    <Button
      type="button"
      size="sm"
      variant={draft.companyMode === companyMode ? 'secondary' : 'ghost'}
      aria-pressed={draft.companyMode === companyMode}
      disabled={locked}
      onClick={() => onChange({ companyMode })}
    >
      {companyMode === 'existing' ? <BuildingIcon /> : <PlusIcon />}
      {companyMode === 'existing' ? 'Existing company' : 'New company'}
    </Button>
  )

  return (
    <DeskSection
      number={1}
      title={sponsor ? 'Sponsor company and contact' : 'Company and contact'}
      description={
        sponsor
          ? 'The sponsor company that takes the package, and the person who receives the documents.'
          : 'The company that books the stand, and the person who receives the documents.'
      }
      done={locked}
      aside={
        <div role="group" aria-label="Company" className="bg-muted/60 flex gap-1 rounded-md p-1">
          {mode('existing')}
          {mode('new')}
        </div>
      }
    >
      {draft.companyMode === 'existing' ? (
        <Field
          label="Company"
          required
          error={issues['company']}
          help={
            hidden === 0
              ? sponsor
                ? 'Type to find a company. Sponsor and exhibitor companies are listed.'
                : 'Type to find a company. Only exhibitor companies are listed.'
              : `Type to find a company. ${hidden} ${hidden === 1 ? 'company has' : 'companies have'} an application already and ${hidden === 1 ? 'is' : 'are'} not listed.`
          }
        >
          {(props) => (
            <Combobox
              {...props}
              value={draft.companyId}
              onValueChange={(companyId) => onChange({ companyId })}
              disabled={locked}
              placeholder="Select a company"
              searchPlaceholder="Company name"
              emptyText="No company has this name. Add it as a new company."
              options={free.map(optionOf)}
            />
          )}
        </Field>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Company name" required error={issues['companyName']}>
            {(props) => (
              <Input
                {...props}
                value={draft.companyName}
                maxLength={200}
                disabled={locked}
                autoComplete="organization"
                onChange={(event) => onChange({ companyName: event.target.value })}
              />
            )}
          </Field>
          <Field label="Business type" help="Optional. For example, Renewable energy.">
            {(props) => (
              <Input
                {...props}
                value={draft.industrySector}
                maxLength={200}
                disabled={locked}
                onChange={(event) => onChange({ industrySector: event.target.value })}
              />
            )}
          </Field>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Contact name" required error={issues['contactName']}>
          {(props) => (
            <Input
              {...props}
              value={draft.contactName}
              maxLength={200}
              disabled={locked}
              autoComplete="name"
              onChange={(event) => onChange({ contactName: event.target.value })}
            />
          )}
        </Field>
        <Field label="Email" required error={issues['contactEmail']}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={draft.contactEmail}
              maxLength={320}
              disabled={locked}
              autoComplete="email"
              onChange={(event) => onChange({ contactEmail: event.target.value })}
            />
          )}
        </Field>
        <Field label="Phone or WhatsApp" help="Optional. Include the country code.">
          {(props) => (
            <Input
              {...props}
              type="tel"
              value={draft.contactPhone}
              maxLength={40}
              disabled={locked}
              autoComplete="tel"
              placeholder="+62 812 3456 7890"
              onChange={(event) => onChange({ contactPhone: event.target.value })}
            />
          )}
        </Field>
      </div>
    </DeskSection>
  )
}

/** A company in the list. Its legal name finds it too, when it has a display name. */
function optionOf(company: Company): ComboboxOption {
  const option: ComboboxOption = { value: company.id, label: company.displayName ?? company.name }
  if (company.displayName !== null) option.keywords = [company.name]
  if (company.countryCode !== null) option.hint = company.countryCode
  return option
}
